import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { ReactElement, useMemo, useRef, useState } from 'react'

import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import ContentfulInternationalSizeConversionGuide from '@src/quince-site-common/src/domains/contentful/contentful-international-size-conversion-guide'
import ProductMeasuringGuideContentful from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import { MergedSizeChart } from '@src/quince-site-common/src/domains/MergedSizeChart'
import keyBy from '@src/quince-site-common/src/utils/keyBy'
import stopPropagationHandler from '@src/quince-site-common/src/utils/stopPropogationHandler'
import { getProductPropertiesForTracking, ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import useLockBodyScroll from '../modal/hooks/useLockBodyScroll'
import SizeChart from '../sizeChart'
import SizeChartHeader from '../sizeChart/components/SizeChartHeader.component'
import SizeChartMergedSizeChartTabs from '../sizeChart/components/SizeChartMergedSizeChartTabs.component'
import Cross from '../sizeChart/icons/Cross.inline.svg'
import styles from '../sizeChart/sizeChart.module.scss'

import { SizeGuideProps } from './SizeGuide'
import { PDPProductSizeGuide, ProductSizeGuide, ProductSizeGuideDesignVariant } from './SizeGuide.types'
import { normalizeSizeChartDefaultUnitOrder } from './sizeGuide.utils'
import { SizeGuideOne } from './sizeGuideOne'
import { TAB_STATES } from './sizeGuideOne/SizeGuideOne'
import { SizeGuideTwo } from './sizeGuideTwo'
import { useSizeGuide } from './useSizeGuide'

const SizeGuideV1 = (props: SizeGuideProps) => {
  const { t } = useTranslation('common')
  const localeConfig = useStatsigDynamicLocaleConfig()
  const showLocalisedReviewUI = Boolean(localeConfig.displayLocalisedReviewSize)
  const internationalSizeConversionGuides =
    props.internationalSizeConversionGuides && props.internationalSizeConversionGuides.length > 0
      ? props.internationalSizeConversionGuides[0]
      : undefined
  const pdpProductSizeGuides: PDPProductSizeGuide[] = getPdpProductSizeGuides(
    props.sizeChart,
    props.mergedSizeChart ?? [],
    props.measuringGuides ?? [],
    internationalSizeConversionGuides,
    showLocalisedReviewUI,
  )
  const sizeContentRef = useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = useState<TAB_STATES>(TAB_STATES.SIZE_CHART)

  const { activeSizeGuideUnitIndex, handleUnitsChange, activePdpProductSizeGuide, handlePdpProductSizeGuideChange } =
    useSizeGuide({
      pdpProductSizeGuides,
    })

  const productSizeGuides = useMemo(
    () => pdpProductSizeGuides.find(ppsz => ppsz.tabName === activePdpProductSizeGuide)?.productSizeGuides ?? [],
    [activePdpProductSizeGuide, pdpProductSizeGuides],
  )

  const activeProductSizeGuide = productSizeGuides[0]
  const activeSizeChart = activeProductSizeGuide?.sizeChart

  const productSizeGuideDesignVariant = useMemo(() => {
    return getProductSizeGuideDesignVariant(productSizeGuides, internationalSizeConversionGuides)
  }, [productSizeGuides, internationalSizeConversionGuides])

  const activeSizeChartSection = activeSizeChart?.sections[activeSizeGuideUnitIndex]
  const isUnitsToggleActive = !!(
    activeSizeChartSection?.title && activeSizeChartSection.title === activeSizeChart?.sections[1]?.title
  )

  const productProperties = props.product ? getProductPropertiesForTracking(props.product) : undefined

  const shouldHideSizeChart = !props.showSizeChartModal || !productSizeGuides.length || !activeSizeChartSection

  useLockBodyScroll(!shouldHideSizeChart, sizeContentRef)

  const onActiveTabChange = (tabName: TAB_STATES) => {
    setActiveTab(tabName)
  }

  if (shouldHideSizeChart) return null

  return (
    <>
      <div onClick={props.onSizeChartModalClose} className={styles.blurModal} />
      <div
        ref={sizeContentRef}
        onTouchMove={stopPropagationHandler}
        className={classnames(styles.wrapper, props.wrapperClassName)}
      >
        <Cross aria-label={t('close_button')} className={styles.crossIcon} onClick={props.onSizeChartModalClose} />

        <SizeChartHeader
          activeTab={activeTab}
          activeSizeChart={activeSizeChart}
          sizeChartSections={activeSizeChart?.sections ?? []}
          selectedSizeChartSection={activeSizeChartSection}
          isMergedSizeChart={pdpProductSizeGuides.length > 1}
          isUnitsToggleActive={isUnitsToggleActive}
          onUnitsToggle={() => {
            const newIndex = activeSizeGuideUnitIndex ? 0 : 1
            handleUnitsChange(newIndex)
            trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE, {
              section_type: 'size_chart_toggle',
              context: {
                size_chart_name: activeProductSizeGuide?.sizeChart.title,
                size_chart_units: activeProductSizeGuide?.sizeChart.sections[newIndex]?.title,
                productProperties: productProperties,
              },
            })
          }}
          hideUnitsTitle
        />

        {pdpProductSizeGuides.length > 1 && (
          <SizeChartMergedSizeChartTabs
            activePdpProductSizeGuide={activePdpProductSizeGuide}
            handlePdpProductSizeGuideChange={handlePdpProductSizeGuideChange}
            pdpProductSizeGuides={pdpProductSizeGuides}
          />
        )}

        {renderProductSizeGuide({
          productSizeGuides,
          productSizeGuideDesignVariant,
          activeSizeGuideUnitIndex,
          productProperties,
          onActiveTabChange,
        })}
      </div>
    </>
  )
}

const getPdpProductSizeGuides = (
  sizeChart: ContentfulSizeChart,
  mergedSizeChart: MergedSizeChart[],
  measuringGuides: ProductMeasuringGuideContentful[],
  internationalSizeConversionGuides: ContentfulInternationalSizeConversionGuide | undefined,
  showLocalisedReviewUI: boolean,
): PDPProductSizeGuide[] => {
  const pdpProductSizeGuides = []
  if (mergedSizeChart.length > 0) {
    mergedSizeChart.forEach(msc => {
      const pSizeGuide = getProductSizeGuides(msc.sizeChart, measuringGuides, undefined, showLocalisedReviewUI)
      if (pSizeGuide.length > 0) {
        pdpProductSizeGuides.push({
          tabName: msc.tabName,
          productSizeGuides: pSizeGuide,
        })
      }
    })
  } else {
    const pSizeGuide = getProductSizeGuides(
      sizeChart,
      measuringGuides,
      internationalSizeConversionGuides,
      showLocalisedReviewUI,
    )
    if (pSizeGuide.length > 0) {
      pdpProductSizeGuides.push({
        tabName: 'pdpProductSizeGuide',
        productSizeGuides: pSizeGuide,
      })
    }
  }
  return pdpProductSizeGuides
}

const getProductSizeGuides = (
  productSizeChart: ContentfulSizeChart,
  productMeasuringGuides: ProductMeasuringGuideContentful[],
  productInternationalSizeConversionGuides: ContentfulInternationalSizeConversionGuide | undefined,
  showLocalisedReviewUI: boolean,
): ProductSizeGuide[] => {
  if (!productSizeChart) return []
  const normalizedSizeChart = (normalizeSizeChartDefaultUnitOrder(productSizeChart, showLocalisedReviewUI) ??
    productSizeChart) as ContentfulSizeChart
  const updatedInternationalSizeGuideSections =
    (productInternationalSizeConversionGuides?.sections?.length ?? 0) > 0
      ? [productInternationalSizeConversionGuides?.sections[0], productInternationalSizeConversionGuides?.sections[0]]
      : []
  const updatedInternationalSizeConversionGuides = productInternationalSizeConversionGuides
    ? ({
        ...productInternationalSizeConversionGuides,
        sections: updatedInternationalSizeGuideSections,
      } as ContentfulInternationalSizeConversionGuide)
    : undefined
  const normalizedInternationalSizeChart =
    normalizeSizeChartDefaultUnitOrder(updatedInternationalSizeConversionGuides, showLocalisedReviewUI) ??
    updatedInternationalSizeConversionGuides
  const productMeasuringGuideKeys = productSizeChart.measuringGuides
  const productMeasuringGuidesByKey: Record<string, ProductMeasuringGuideContentful> = keyBy(
    productMeasuringGuides,
    'title', // eslint-disable-line i18next/no-literal-string
  )
  return productMeasuringGuideKeys?.length
    ? productMeasuringGuideKeys.map(msk => ({
        sizeChart: normalizedSizeChart,
        measuringGuide: productMeasuringGuidesByKey[msk],
        internationalSizeChart: normalizedInternationalSizeChart,
      }))
    : [
        {
          sizeChart: normalizedSizeChart,
          measuringGuide: undefined,
          internationalSizeChart: normalizedInternationalSizeChart,
        },
      ]
}

const getProductSizeGuideDesignVariant = (
  productSizeGuides: ProductSizeGuide[],
  internationalSizeConversionGuides: ContentfulInternationalSizeConversionGuide | undefined,
): ProductSizeGuideDesignVariant | undefined => {
  let psgVariant
  if (productSizeGuides.length > 1) {
    psgVariant = ProductSizeGuideDesignVariant.DESIGN_2
  } else if (productSizeGuides[0]?.measuringGuide || internationalSizeConversionGuides) {
    psgVariant = ProductSizeGuideDesignVariant.DESIGN_1
  }
  return psgVariant
}

const renderProductSizeGuide = ({
  productSizeGuides,
  productSizeGuideDesignVariant,
  activeSizeGuideUnitIndex,
  productProperties,
  onActiveTabChange,
}: {
  productSizeGuides: ProductSizeGuide[]
  productSizeGuideDesignVariant?: ProductSizeGuideDesignVariant
  activeSizeGuideUnitIndex: number
  productProperties?: ProductProperties
  onActiveTabChange: (tabName: TAB_STATES) => void
}): ReactElement | null => {
  switch (productSizeGuideDesignVariant) {
    case ProductSizeGuideDesignVariant.DESIGN_1:
      return (
        <SizeGuideOne
          productSizeGuides={productSizeGuides}
          activeSizeGuideUnitIndex={activeSizeGuideUnitIndex}
          productProperties={productProperties}
          onActiveTabChange={onActiveTabChange}
        />
      )
    case ProductSizeGuideDesignVariant.DESIGN_2:
      return (
        <SizeGuideTwo
          productSizeGuides={productSizeGuides}
          activeSizeGuideUnitIndex={activeSizeGuideUnitIndex}
          productProperties={productProperties}
        />
      )
    default:
      return (
        <SizeChart
          productSizeGuides={productSizeGuides}
          activeSizeGuideUnitIndex={activeSizeGuideUnitIndex}
          sizeChart={productSizeGuides && productSizeGuides.length > 0 ? productSizeGuides[0]?.sizeChart : undefined}
        />
      )
  }
}

export default SizeGuideV1
